import type { ChartConfiguration } from "chart.js"
import { FC, useMemo } from "react"
import { format } from "date-fns/esm"
import { AITrainingMetric } from "../../../../../../../../Models/API/AIModel"
import { Color } from "../../../../../../../../Models/Client/Color"
import { Chart } from "../../../../../../../Common/Lazy/Chart"
import { Text } from "dash-styled"

interface TrainingPerformanceProps {
    aiMetric: AITrainingMetric
}

export const TrainingPerformance: FC<TrainingPerformanceProps> = ({
    aiMetric
}) => {
    const metricValues = useMemo<number[]>(
        () => ((aiMetric.values as any)?.["metric"] ?? []).map(parseFloat),
        [aiMetric.values]
    )

    const timestamps = useMemo(
        () =>
            ((aiMetric.values as any)?.["timestamp"] ?? []).map(
                Date.parse
            ) as number[],
        [aiMetric.values]
    )

    const dateTimestamps = useMemo(
        () =>
            timestamps.reduce((agg, timestamp, i) => {
                if (i === 0) {
                    agg[i] = format(timestamp, "d MMM yyyy")
                    return agg
                }
                const formattedTimestamp = format(timestamp, "d MMM yyyy")
                if (formattedTimestamp === agg[0]) {
                    agg[i] = ""
                } else {
                    agg[i] = formattedTimestamp
                }
                return agg
            }, [] as string[]),
        [timestamps]
    )
    const aiMetricConfig: ChartConfiguration | undefined = useMemo(() => {
        if (metricValues.length === 0) return undefined

        const config: ChartConfiguration = {
            type: "line",
            data: {
                labels: timestamps.map(timestamp => format(timestamp, "H:m:s")),
                datasets: [
                    {
                        data: metricValues,
                        borderColor: Color.violet,
                        lineTension: 0,
                        backgroundColor: "transparent"
                    }
                ]
            },
            options: {
                animation: {
                    duration: 0
                },
                scales: {
                    xAxes: [
                        {
                            gridLines: {
                                drawTicks: false,
                                color: "rgba(0,0,0,0)",
                                zeroLineColor: "#979797"
                            },
                            ticks: {
                                max: 3,
                                padding: 10
                            }
                        }
                    ],
                    yAxes: [
                        {
                            gridLines: {
                                drawTicks: false,
                                color: "rgba(0,0,0,0)",
                                zeroLineColor: "#979797"
                            },
                            ticks: {
                                stepSize: 1,
                                beginAtZero: true,
                                padding: 10,
                                maxTicksLimit: 5
                            }
                        }
                    ]
                },
                legend: {
                    display: false
                },
                plugins: {
                    datalabels: {
                        labels: {
                            title: null
                        }
                    }
                },
                maintainAspectRatio: false,
                responsive: true
            }
        }
        return config
    }, [metricValues, timestamps])

    return (
        <div className="border border-solid border-rhino-300 rounded p-4 pb-1 h-full flex flex-col justify-start">
            <Text weight="semibold" component="span">
                {aiMetric.metric_name}
            </Text>
            <div className="flex-1">
                <Chart config={aiMetricConfig} />
            </div>
            <div className="flex justify-between my-2">
                {dateTimestamps.map((d, i) => (
                    <span key={i} style={{ fontSize: "0.625rem" }}>
                        {d}
                    </span>
                ))}
            </div>
        </div>
    )
}
